import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { handleApiError } from './errorHandler';

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Normalize ID parameter - handles both numeric IDs and dashless UUID strings.
 * parseInt on a UUID like "550e8400e29b41d4a716446655440000" returns a partial
 * number (550), so we need to detect UUIDs before parsing.
 */
const normalizeId = (id) => {
  if (typeof id === 'string' && /^[0-9a-f]{32}$/i.test(id)) return id;
  return parseInt(id, 10) || id;
};

// Request interceptor to add JWT token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration, network errors, and common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle API errors globally (shows notifications, redirects on 401)
    handleApiError(error);
    // Always reject the error so individual API calls can handle errors if needed
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Users API
export const usersAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUserById: (id) => api.get(`/users/${parseInt(id, 10) || id}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${parseInt(id, 10) || id}`, data),
  updatePassword: (id, currentPassword, newPassword) => api.put(`/users/${parseInt(id, 10) || id}/password`, { currentPassword, newPassword }),
  // Admin reset: set a new password without the current one (backend skips the
  // currentPassword check when an Administrator changes another user's password).
  adminResetPassword: (id, newPassword) => api.put(`/users/${normalizeId(id)}/password`, { newPassword }),
  deleteUser: (id) => api.delete(`/users/${parseInt(id, 10) || id}`),
  permanentDeleteUser: (id) => api.delete(`/users/${normalizeId(id)}/permanent`),
  updateToken: (id) => api.put(`/users/${parseInt(id, 10) || id}/token`),
  uploadAvatar: (id, formData) => api.put(`/users/${parseInt(id, 10) || id}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteAvatar: (id) => api.delete(`/users/${parseInt(id, 10) || id}/avatar`),
  enrollTrainee: (userId, moduleId) => api.post('/users/enroll', { traineeId: parseInt(userId, 10) || userId, moduleId: parseInt(moduleId, 10) || moduleId }),
  getUserModules: (userId) => api.get(`/users/${parseInt(userId, 10) || userId}/modules`),
};

// Content API
export const contentAPI = {
  getContent: (params) => api.get('/content', { params }),
  getContentById: (id) => api.get(`/content/${parseInt(id, 10) || id}`),
  createContent: (data) => api.post('/content', data),
  createContentWithFiles: (formData) => api.post('/content/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateContent: (id, data) => api.put(`/content/${parseInt(id, 10) || id}`, data),
  deleteContent: (id) => api.delete(`/content/${parseInt(id, 10) || id}`),
  uploadAPK: (id, formData) => api.post(`/content/${parseInt(id, 10) || id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadThumbnail: (id, formData) => api.post(`/content/${parseInt(id, 10) || id}/thumbnail`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteThumbnail: (id) => api.delete(`/content/${parseInt(id, 10) || id}/thumbnail`),
  downloadAPK: (id) => api.get(`/content/${parseInt(id, 10) || id}/download`, { responseType: 'blob' }),
  completeContent: (id) => api.post(`/content/${parseInt(id, 10) || id}/complete`),
  getCategories: () => api.get('/content/categories'),
  getPlatforms: () => api.get('/content/platforms'),
  getStats: () => api.get('/content/stats'),
};

// Explainer Videos API
export const explainerVideosAPI = {
  getVideos: (params) => api.get('/explainer-videos', { params }),
  getVideoById: (id) => api.get(`/explainer-videos/${parseInt(id, 10) || id}`),
  createVideo: (data) => api.post('/explainer-videos', data),
  updateVideo: (id, data) => api.put(`/explainer-videos/${parseInt(id, 10) || id}`, data),
  deleteVideo: (id) => api.delete(`/explainer-videos/${parseInt(id, 10) || id}`),
  uploadThumbnail: (id, formData) => api.post(`/explainer-videos/${parseInt(id, 10) || id}/thumbnail`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteThumbnail: (id) => api.delete(`/explainer-videos/${parseInt(id, 10) || id}/thumbnail`),
  getCategories: () => api.get('/explainer-videos/categories'),
  getStats: () => api.get('/explainer-videos/stats'),
  getContentExplainerVideos: (contentId) => api.get(`/explainer-videos/content/${parseInt(contentId, 10) || contentId}/explainer-videos`),
  addVideoToContent: (contentId, videoId, orderIndex = 0) => api.post(`/explainer-videos/content/${parseInt(contentId, 10) || contentId}/explainer-videos`, { videoId: parseInt(videoId, 10) || videoId, orderIndex }),
  removeVideoFromContent: (contentId, videoId) => api.delete(`/explainer-videos/content/${parseInt(contentId, 10) || contentId}/explainer-videos/${parseInt(videoId, 10) || videoId}`),
};

// Modules API
export const modulesAPI = {
  getModules: (params) => api.get('/modules', { params }),
  getModuleById: (id) => api.get(`/modules/${parseInt(id, 10) || id}`),
  createModule: (data) => api.post('/modules', data),
  updateModule: (id, data) => api.put(`/modules/${parseInt(id, 10) || id}`, data),
  deleteModule: (id) => api.delete(`/modules/${parseInt(id, 10) || id}`),
  addContentToModule: (moduleId, contentId) => api.post(`/modules/${parseInt(moduleId, 10) || moduleId}/content`, { contentId: parseInt(contentId, 10) || contentId }),
  removeContentFromModule: (moduleId, contentId) => api.delete(`/modules/${parseInt(moduleId, 10) || moduleId}/content/${parseInt(contentId, 10) || contentId}`),
  assignInstructor: (moduleId, instructorId) => api.post(`/modules/${parseInt(moduleId, 10) || moduleId}/instructors`, { instructorId: parseInt(instructorId, 10) || instructorId }),
  removeInstructor: (moduleId, instructorId) => api.delete(`/modules/${parseInt(moduleId, 10) || moduleId}/instructors/${parseInt(instructorId, 10) || instructorId}`),
  enrollTrainee: (moduleId, traineeId) => api.post(`/modules/${parseInt(moduleId, 10) || moduleId}/enroll`, { traineeId: parseInt(traineeId, 10) || traineeId }),
  unenrollTrainee: (moduleId, traineeId) => api.delete(`/modules/${parseInt(moduleId, 10) || moduleId}/enroll/${parseInt(traineeId, 10) || traineeId}`),
  getModuleProgress: (id) => api.get(`/modules/${parseInt(id, 10) || id}/progress`),
  getModuleTrainees: (id) => api.get(`/modules/${parseInt(id, 10) || id}/trainees`),
  getCategories: () => api.get('/modules/categories'),
  getLevels: () => api.get('/modules/levels'),
};

// Analytics API
export const analyticsAPI = {
  getOverview: () => api.get('/analytics/overview'),
  getModuleAnalytics: (moduleId) => api.get(`/analytics/module/${parseInt(moduleId, 10) || moduleId}`),
  getPersonalAnalytics: () => api.get('/analytics/personal'),
  getContentAnalytics: () => api.get('/analytics/content'),
  getDashboardStats: () => api.get('/dashboard/stats'),
  getTraineeAnalytics: (username) => api.get(`/analytics/trainee/${encodeURIComponent(username)}`),
  getTraineeLeaderboard: () => api.get('/analytics/trainees/leaderboard'),
  // Drill-down analytics (read the VR event stream + assessment results)
  getPersonalModuleAnalytics: (moduleId) => api.get(`/analytics/personal/module/${normalizeId(moduleId)}`),
  getTraineeDetailAnalytics: (id) => api.get(`/analytics/trainee/${normalizeId(id)}/detail`),
  getTraineeModuleAnalytics: (traineeId, moduleId) =>
    api.get(`/analytics/trainee/${normalizeId(traineeId)}/module/${normalizeId(moduleId)}`),
  getTraineeContentAnalytics: (traineeId, moduleId, contentId) =>
    api.get(`/analytics/trainee/${normalizeId(traineeId)}/module/${normalizeId(moduleId)}/content/${normalizeId(contentId)}`),
};

// Enrollment API
export const enrollmentsAPI = {
  bulkEnroll: (data) => api.post('/enrollments/bulk', data),
  getUserEnrollments: (userId) => api.get(`/enrollments/user/${parseInt(userId, 10) || userId}`),
  updateProgress: (userId, moduleId, progress) => api.put(`/enrollments/${parseInt(userId, 10) || userId}/${parseInt(moduleId, 10) || moduleId}`, { progress }),
  completeModule: (userId, moduleId) => api.post(`/enrollments/${parseInt(userId, 10) || userId}/${parseInt(moduleId, 10) || moduleId}/complete`),
};

// System API
export const systemAPI = {
  healthCheck: () => api.get('/system/health'),
  getConfig: () => api.get('/system/config'),
  updateConfig: (data) => api.put('/system/config', data),
  getPermissions: () => api.get('/system/permissions'),
};

// Notifications API (in-app notifications)
export const notificationsAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.patch(`/notifications/${normalizeId(id)}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
};

// VR Access API (Meta Developer Hub release channels)
export const vrAccessAPI = {
  // Channels
  listChannels: (params) => api.get('/vr-access/channels', { params }),
  getChannel: (id) => api.get(`/vr-access/channels/${normalizeId(id)}`),
  createChannel: (data) => api.post('/vr-access/channels', data),
  updateChannel: (id, data) => api.put(`/vr-access/channels/${normalizeId(id)}`, data),
  deleteChannel: (id) => api.delete(`/vr-access/channels/${normalizeId(id)}`),

  // Users
  listUsers: (params) => api.get('/vr-access/users', { params }),
  getUser: (id) => api.get(`/vr-access/users/${normalizeId(id)}`),
  createUser: (data) => api.post('/vr-access/users', data),
  updateUser: (id, data) => api.put(`/vr-access/users/${normalizeId(id)}`, data),
  resendInvite: (id) => api.post(`/vr-access/users/${normalizeId(id)}/resend`),
  revokeInvite: (id) => api.post(`/vr-access/users/${normalizeId(id)}/revoke`),
  deleteUser: (id) => api.delete(`/vr-access/users/${normalizeId(id)}`),
};

// Virtual Tours API (curated web-based virtual tours)
export const virtualToursAPI = {
  getTours: (params) => api.get('/virtual-tours', { params }),
  getTourById: (id) => api.get(`/virtual-tours/${normalizeId(id)}`),
  createTour: (data) => api.post('/virtual-tours', data),
  updateTour: (id, data) => api.put(`/virtual-tours/${normalizeId(id)}`, data),
  deleteTour: (id) => api.delete(`/virtual-tours/${normalizeId(id)}`),
  getCategories: () => api.get('/virtual-tours/categories'),
  uploadImage: (formData) => api.post('/virtual-tours/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export default api;